import { useCallback, useEffect, useState } from "react";
import { Box, Button, FormGroup, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "commons/store/hooks";
import { CheckboxFormik, TextFieldFormik } from "components/Form";
import { register } from "core/redux/userSlice";
import { Form, Formik } from "formik";
import { useSnackbar} from "notistack";
import { Navigate, useNavigate } from "react-router-dom";
import ColoredBox from "components/ColoredBox";
import yup from "commons/yup";
import { Link } from "react-router-dom";
import { ROUTES_DASHBOARD } from "commons/constants/paths";
import { ROUTES_CORE } from "commons/constants/paths";
import { PROJECT_NAME } from "commons/constants/env";
import { getToken, isTokenExpired } from "commons/auth/tokens";
import { useTranslation } from "react-i18next";
import { fetchAgreements, selectAgreements } from "../../redux/agreementSlice";
import { Agreement } from "types/agreement/Agreement";
import { LoadingButton } from "@mui/lab";
import { Violation } from "utils/dataAccess";

const Register = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const token = getToken();
  const { t } = useTranslation();

  const [visiblePassword, setVisiblePassword] = useState(false);
  const togglePasswordVisible = useCallback(() => {
    setVisiblePassword((visible) => !visible);
  }, []);

  const [isDataLoading, setDataLoading] = useState(false);
  const [isProcessing, setProcessing] = useState(false)

  const agreements = useAppSelector(selectAgreements);

  const validationSchema = yup.object({
    firstname: yup.string()
      .min(2, t("Firstname must be at least 2 characters"))
      .max(50, t("Firstname must be at most 50 characters"))
      .required(t("Firstname is required")),
    lastname: yup.string()
      .min(2, t("Lastname must be at least 2 characters"))
      .max(50, t("Lastname must be at most 50 characters"))
      .required(t("Lastname is required")),
    email: yup
      .string()
      .email(t("Invalid Email Address"))
      .required(t("Email is required")),
    phone: yup
      .string()
      .matches(new RegExp("[0-9]{9}"), t("Invalid phone number"))
      .min(9, t("Phone number must be 9 digits long"))
      .max(9, t("Phone number must be 9 digits long"))
      .required(t("Phone is required")),
    plainPassword: yup
      .string()
      .min(8, t("Password must be 8 characters long"))
      .required(t("Password is required")),
    repeatPlainPassword: yup
      .string()
      .oneOf([yup.ref("plainPassword")], t("Passwords must match"))
      .required(t("Please re-type password")),
    addresses: yup.array().of(
      yup.object().shape({
        street: yup.string().required(t("Street is required")),
        number: yup.string().required(t("Number is required")),
        postcode: yup
          .string()
          .matches(/^[0-9]{2}[-][0-9]{3}$/, t("Postcode is invalid"))
          .required(t("Postcode is required")),
        city: yup.string().required(t("City is required")),
        // city: yup.object().nullable().typeError(t("City is required")).required(),
      }),
    ),
    agreements: yup
      .array()
      .transform((_, orig) => {
        orig?.forEach((value: any, index: any) => {
          if (agreements?.member?.[index]?.required === false) {
            orig[index] = true;
          }
        });

        return orig;
      })
      .of(
        yup
          .bool()
          .test(
            "agreement",
            t("You have to agree the consent!"),
            (value) => value === true,
          )
          .required(t("You have to agree the consent!")),
      ),
  });

  const initialValues = {
    firstname: "",
    lastname: "",
    email: "",
    plainPassword: "",
    phone: "",
    addresses: [{
      street: "",
      number: "",
      postcode: "",
      city: "",
    }],
    agreements: agreements?.member?.map(() => false),
  };

  // @ts-ignore
  const handleSubmit = async (values: typeof initialValues, { setStatus, setErrors, resetForm }) => {
    setProcessing(true);

    const data: any = { ...values };

    if (values["agreements"]) {
      const a: any[] = [];
      values["agreements"]?.forEach((value: any, index: any) => {
        const cA = agreements?.member?.[index];
        a.push({
          agreement: `/api/agreements/${cA?.id}`,
          agree: value,
        });
      });

      data["agreements"] = a;
    }

    try {
      await dispatch(register(data));

      setStatus({ isValid: true });

      resetForm();

      enqueueSnackbar(t('Registration was successful'), { variant: 'success' });

      navigate(ROUTES_CORE.LOGIN);
    } catch (error: any) {
      setStatus({
        isValid: false,
        msg: `${"status" in error ? error.status : error.message}`,
      });

      if (!error.violations) enqueueSnackbar(t('invalid-data-entered-please-re-enter-the-correct-details'), {
        variant: 'error',
      });

      const fields: { [key: string]: string } = {};
      error.violations.map(
        (violation: Violation) => (fields[violation.propertyPath] = violation.message)
      );

      if (fields) {
        // @ts-ignore
        setErrors(fields);
      }
    }

    setProcessing(false);
  };

  const fetchAgreementData = async () => {
    setDataLoading(true);
    await dispatch(fetchAgreements('user_register')).unwrap();
    setDataLoading(false);
  }

  useEffect(() => {
    fetchAgreementData().then();
  }, []);

  if (token && !isTokenExpired(token)) {
    return <Navigate to={ROUTES_DASHBOARD.DASHBOARD}/>;
  }

  return (
    <Box
      minHeight="100vh"
      display="flex"
      justifyContent="center"
      alignItems="center"
      pl={2}
      pr={2}
    >
      <ColoredBox p={5} width={352} maxWidth="100%">
        <Typography variant="body2">{PROJECT_NAME}</Typography>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          <Form>
            <Typography variant="h5">{t('Registration')}</Typography>

            <Box mt={3.5}>
              <TextFieldFormik
                margin="dense"
                name="firstname"
                label={t('firstname')}
                type="text"
                variant="outlined"
                fullWidth
              />
              <TextFieldFormik
                margin="dense"
                name="lastname"
                label={t('lastname')}
                type="text"
                variant="outlined"
                fullWidth
              />
              <TextFieldFormik
                margin="dense"
                name="email"
                label={t('email')}
                type="email"
                variant="outlined"
                fullWidth
              />
              <TextFieldFormik
                margin="dense"
                name="phone"
                label={t('phone')}
                type="tel"
                variant="outlined"
                fullWidth
              />
              <TextFieldFormik
                margin="dense"
                name="plainPassword"
                label={t('password')}
                type={visiblePassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
              />
              <TextFieldFormik
                margin="dense"
                name="repeatPlainPassword"
                placeholder="*********"
                label={t('Repeat password')}
                type={visiblePassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
              />
              <Button
                size={"small"}
                onClick={() => setVisiblePassword(!visiblePassword)}
                color={'primary'}
              >
                {visiblePassword ? t('hide-password') : t('show-password')}
              </Button>
              <TextFieldFormik
                margin="dense"
                name="addresses[0].street"
                label={t('street')}
                type="text"
                variant="outlined"
                fullWidth
              />
              <TextFieldFormik
                margin="dense"
                name="addresses[0].number"
                label={t('number')}
                type="text"
                variant="outlined"
                fullWidth
              />
              <TextFieldFormik
                margin="dense"
                name="addresses[0].postcode"
                label={t('postcode')}
                type="text"
                variant="outlined"
                fullWidth
              />
              <TextFieldFormik
                margin="dense"
                name="addresses[0].city"
                label={t('city')}
                type="text"
                variant="outlined"
                fullWidth
              />
              <FormGroup>
                {agreements?.['member']?.map((item: Agreement, index: any) => (
                  <div className="agreement" key={index}>
                    <CheckboxFormik
                      name={`agreements.${index}`}
                      size="small"
                      color="secondary"
                      label={
                        <Box
                          flexWrap="wrap"
                          alignItems="center"
                          justifyContent="flex-start"
                          gap={1}
                        >
                          <span dangerouslySetInnerHTML={{ __html: item.content }}></span>
                        </Box>
                      }
                      value={item?.id}
                    />
                  </div>
                ))}
              </FormGroup>
            </Box>
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Box>
                <LoadingButton
                  type="submit"
                  color="primary"
                  variant="contained"
                  loading={isDataLoading || isProcessing}
                >
                  {t('Register')}
                </LoadingButton>
              </Box>
            </Box>
            <Box mt={1.5} display="flex" justifyContent="flex-end">
              <Button
                color="primary"
                component={Link}
                to={ROUTES_CORE.LOGIN}
                sx={{ textTransform: 'unset' }}
              >
                {t('login')}
              </Button>
            </Box>
          </Form>
        </Formik>
      </ColoredBox>
    </Box>
  );
};

export default Register;